import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { useLang, tr } from "../contexts/LangContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { MapPin, Phone, Mail, Clock, Building2, Check, Facebook, Instagram, Youtube, ChevronDown } from "lucide-react";

export default function Contact() {
  const { t, lang, visible, sectionOrder } = useLang();
  const ord = (id) => sectionOrder("contact", id, ["info", "map", "social", "bank"]);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({});
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data || {})).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data || [])).catch(() => {});
  }, []);

  const phone = settings.phone || "+32 4 264 00 00";
  const email = settings.email || "info@rycm.be";
  const iban = (settings.iban || "BE71 0961 2345 6769").replace(/^IBAN\s*:?\s*/i, "");
  const bic = settings.bic || "GKCCBEBB";
  const address = settings[`address_${lang}`] || settings.address_fr || t.contact.addressValue;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/contact", { ...form, lang });
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow={t.contact.eyebrow} title={t.contact.title} lead={t.contact.lead} />

      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label={t.contact.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
                <Field label={t.contact.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
              </div>
              <Field label={t.contact.subject} value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} required testid="contact-subject" />
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{t.contact.message}</label>
                <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border-b border-slate-300 focus:border-navy outline-none py-3 bg-transparent resize-none" data-testid="contact-message" />
              </div>
              {err && <div className="text-sm text-red-600" data-testid="contact-error">{err}</div>}
              {sent && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-4 py-3" data-testid="contact-success">
                  <Check className="w-4 h-4" /> {t.contact.success}
                </div>
              )}
              <button type="submit" disabled={submitting} className="inline-block bg-navy text-white px-10 py-4 text-xs uppercase tracking-[0.25em] hover:bg-club-red transition-colors disabled:opacity-60" data-testid="contact-submit">
                {t.contact.send}
              </button>
            </form>

            {/* FAQ */}
            {visible("contact", "faq") && faqs.length > 0 && (
              <div className="mt-16" data-testid="contact-faq">
                <div className="label-eyebrow text-club-red mb-4">{t.contact.faqTitle}</div>
                <h2 className="font-serif text-2xl lg:text-3xl font-light text-navy">{t.contact.faqTitle}</h2>
                <div className="divider-gold mt-4 mb-6" />
                <div className="space-y-2">
                  {faqs.map((f) => (
                    <FaqItem key={f.id} faq={f} lang={lang} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-slate-200 flex flex-col">
            {visible("contact", "info") && (
              <div style={{ order: ord("info") }} className="space-y-7">
                <InfoBlock icon={MapPin} label={t.contact.address} value={address} />
                <InfoBlock icon={Phone} label={t.contact.phone} value={phone} />
                <InfoBlock icon={Mail} label={tr("Email")} value={email} />
                <InfoBlock icon={Clock} label={t.contact.hours} value={t.contact.hoursValue} />
              </div>
            )}

            {/* Map */}
            {visible("contact", "map") && (
              <div style={{ order: ord("map") }} className="mt-8 aspect-video overflow-hidden border border-slate-200" data-testid="contact-map">
                <iframe
                  title="RYCM location"
                  width="100%" height="100%"
                  src="https://maps.google.com/maps?q=Île+Monsin+4020+Liège&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  loading="lazy"
                />
              </div>
            )}

            {/* Social */}
            {visible("contact", "social") && (
              <div style={{ order: ord("social") }} className="mt-8">
                <div className="text-xs uppercase tracking-[0.25em] text-club-red mb-3 font-semibold">{t.contact.followUs}</div>
                <div className="flex items-center gap-4">
                  <a href={settings.facebook_url || "https://facebook.com"} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-navy text-white flex items-center justify-center hover:bg-club-red transition-colors" data-testid="contact-fb"><Facebook className="w-4 h-4" /></a>
                  <a href={settings.instagram_url || "https://instagram.com"} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-navy text-white flex items-center justify-center hover:bg-club-red transition-colors" data-testid="contact-ig"><Instagram className="w-4 h-4" /></a>
                  <a href={settings.youtube_url || "https://youtube.com"} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-navy text-white flex items-center justify-center hover:bg-club-red transition-colors" data-testid="contact-yt"><Youtube className="w-4 h-4" /></a>
                </div>
              </div>
            )}

            {visible("contact", "bank") && (
              <div style={{ order: ord("bank") }} className="mt-10 bg-navy text-white p-8" data-testid="bank-details">
                <Building2 className="w-6 h-6 text-club-yellow mb-4" strokeWidth={1.5} />
                <div className="label-eyebrow text-club-yellow mb-2">{t.contact.bankTitle}</div>
                <h3 className="font-serif text-xl mb-3">{t.contact.bankSub}</h3>
                <div className="divider-gold mb-6" />
                <dl className="text-sm space-y-3">
                  <Row label={t.contact.bankHolder} value={t.contact.bankHolderValue} />
                  <Row label={t.contact.iban} value={iban} mono />
                  <Row label={t.contact.bic} value={bic} mono />
                  <Row label={t.contact.communication} value={t.contact.communicationValue} />
                </dl>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ faq, lang }) {
  const [open, setOpen] = useState(false);
  const q = faq[`question_${lang}`] || faq.question_fr;
  const a = faq[`answer_${lang}`] || faq.answer_fr;
  return (
    <div className="border border-slate-200" data-testid={`faq-public-${faq.id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-sand transition-colors" data-testid={`faq-toggle-${faq.id}`}>
        <span className="font-serif text-lg text-navy">{q}</span>
        <ChevronDown className={`w-4 h-4 text-club-red shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed whitespace-pre-line" data-testid={`faq-answer-public-${faq.id}`}>{a}</div>}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border-b border-slate-300 focus:border-navy outline-none py-3 bg-transparent" data-testid={testid} />
    </div>
  );
}

function InfoBlock({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <Icon className="w-5 h-5 text-club-yellow mt-1 shrink-0" strokeWidth={1.5} />
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-1">{label}</div>
        <div className="text-navy">{value}</div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 gap-2 border-b border-white/10 pb-2">
      <dt className="col-span-1 text-xs uppercase tracking-widest text-slate-400">{label}</dt>
      <dd className={`col-span-2 ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}
